import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import {
  Users, Mail, Phone, Clock, Search, Filter, RefreshCw, LogOut, Eye, Trash2, 
  Bell, CheckCircle, XCircle, Calendar, Globe, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboardPage = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, read_leads: 0, today_leads: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [lastChecked, setLastChecked] = useState(Date.now());
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    const expiry = localStorage.getItem('tokenExpiry');
    
    if (!token || !expiry || Date.now() > parseInt(expiry)) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('tokenExpiry');
      navigate('/admin');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchLeads = useCallback(async () => {
    if (!checkAuth()) return;
    
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterStatus === 'unread') params.append('is_read', 'false');
      if (filterStatus === 'read') params.append('is_read', 'true');
      
      const response = await axios.get(`${API_URL}/api/admin/leads?${params}`, {
        headers: getAuthHeaders()
      });
      setLeads(response.data);
    } catch (err) {
      console.error('Error fetching leads:', err);
      if (err.response?.status === 401) {
        navigate('/admin');
      }
    }
  }, [checkAuth, search, filterStatus, navigate]);

  const fetchStats = useCallback(async () => {
    if (!checkAuth()) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [checkAuth]);

  const checkNewLeads = useCallback(async () => {
    if (!checkAuth()) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads/new/count`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.count > newLeadsCount && lastChecked !== Date.now()) {
        // New leads notification
        if (Notification.permission === 'granted') {
          new Notification('New Lead Received!', {
            body: `You have ${response.data.count} unread leads`,
            icon: '/favicon.ico'
          });
        }
      }
      setNewLeadsCount(response.data.count);
      setLastChecked(Date.now());
    } catch (err) {
      console.error('Error checking new leads:', err);
    }
  }, [checkAuth, newLeadsCount, lastChecked]);

  useEffect(() => {
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats()]);
      setLoading(false);
    };
    init();
    
    // Poll for new leads every 30 seconds
    const interval = setInterval(() => {
      checkNewLeads();
      fetchLeads();
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchLeads, fetchStats, checkNewLeads]);

  useEffect(() => {
    fetchLeads();
  }, [search, filterStatus, fetchLeads]);

  const markAsRead = async (leadId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/leads/${leadId}/read`, {}, {
        headers: getAuthHeaders()
      });
      fetchLeads();
      fetchStats();
    } catch (err) {
      console.error('Error marking lead as read:', err);
    }
  };

  const markAsUnread = async (leadId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/leads/${leadId}/unread`, {}, {
        headers: getAuthHeaders()
      });
      fetchLeads();
      fetchStats();
    } catch (err) {
      console.error('Error marking lead as unread:', err);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/leads/${leadId}`, {
        headers: getAuthHeaders()
      });
      setSelectedLead(null);
      fetchLeads();
      fetchStats();
    } catch (err) {
      console.error('Error deleting lead:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('tokenExpiry');
    navigate('/admin');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy-900">
      {/* Header */}
      <header className="bg-navy-800 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-white">Lead Management</h1>
                <p className="text-slate-400 text-sm">{localStorage.getItem('adminEmail')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* New Leads Badge */}
              {stats.new_leads > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <Bell className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-400 text-sm font-medium">{stats.new_leads} new</span>
                </div>
              )}
              
              <Button
                onClick={() => { fetchLeads(); fetchStats(); }}
                variant="outline"
                className="border-white/10 text-slate-300 hover:bg-white/5"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-navy-800 border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Leads</p>
                <p className="font-heading font-bold text-2xl text-white">{stats.total_leads}</p>
              </div>
            </div>
          </div>
          <div className="bg-navy-800 border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">New Leads</p>
                <p className="font-heading font-bold text-2xl text-white">{stats.new_leads}</p>
              </div>
            </div>
          </div>
          <div className="bg-navy-800 border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Read</p>
                <p className="font-heading font-bold text-2xl text-white">{stats.read_leads}</p>
              </div>
            </div>
          </div>
          <div className="bg-navy-800 border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Today</p>
                <p className="font-heading font-bold text-2xl text-white">{stats.today_leads}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-navy-800 border border-white/5 rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-navy-900 border border-white/10 rounded-lg text-white"
              >
                <option value="all">All Leads</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-slate-400">Loading leads...</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No leads found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-navy-900/50 border-b border-white/5">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Service</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Source</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">IP Address</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className={`border-b border-white/5 hover:bg-navy-900/30 transition-colors cursor-pointer ${!lead.is_read ? 'bg-blue-500/5' : ''}`}
                      onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                    >
                      <td className="px-4 py-4">
                        {lead.is_read ? (
                          <span className="flex items-center gap-1 text-green-400 text-sm">
                            <CheckCircle className="w-4 h-4" /> Read
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-yellow-400 text-sm">
                            <Bell className="w-4 h-4" /> New
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-white font-medium">{lead.name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <p className="text-slate-300 text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {lead.email}
                          </p>
                          <p className="text-slate-400 text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {lead.phone}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full">
                          {lead.service_type}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-slate-400 text-sm">{lead.source_page || 'Unknown'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-slate-400 text-sm font-mono">{lead.ip_address || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-slate-400 text-sm">{formatDate(lead.created_at)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {lead.is_read ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); markAsUnread(lead.id); }}
                              className="p-2 hover:bg-white/5 rounded-lg text-slate-400 hover:text-yellow-400"
                              title="Mark as unread"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); markAsRead(lead.id); }}
                              className="p-2 hover:bg-white/5 rounded-lg text-slate-400 hover:text-green-400"
                              title="Mark as read"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteLead(lead.id); }}
                            className="p-2 hover:bg-white/5 rounded-lg text-slate-400 hover:text-red-400"
                            title="Delete lead"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Lead Detail Panel */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
            <div className="bg-navy-800 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl text-white">Lead Details</h2>
                <button onClick={() => setSelectedLead(null)} className="text-slate-400 hover:text-white">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Name</p>
                    <p className="text-white font-medium">{selectedLead.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Status</p>
                    {selectedLead.is_read ? (
                      <span className="inline-flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-4 h-4" /> Read
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-yellow-400">
                        <Bell className="w-4 h-4" /> New
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm mb-1 flex items-center gap-1"><Mail className="w-4 h-4" /> Email</p>
                    <a href={`mailto:${selectedLead.email}`} className="text-blue-400 hover:underline">{selectedLead.email}</a>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1 flex items-center gap-1"><Phone className="w-4 h-4" /> Phone</p>
                    <a href={`tel:${selectedLead.phone}`} className="text-blue-400 hover:underline">{selectedLead.phone}</a>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Service Type</p>
                    <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-sm rounded-full">{selectedLead.service_type}</span>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1 flex items-center gap-1"><Globe className="w-4 h-4" /> Source Page</p>
                    <p className="text-white">{selectedLead.source_page || 'Unknown'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm mb-1 flex items-center gap-1"><Clock className="w-4 h-4" /> Submitted</p>
                    <p className="text-white">{formatDate(selectedLead.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1 flex items-center gap-1"><Globe className="w-4 h-4" /> IP Address</p>
                    <p className="text-white font-mono">{selectedLead.ip_address || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-2 flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Message</p>
                  <div className="bg-navy-900 border border-white/10 rounded-lg p-4">
                    <p className="text-white whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  {selectedLead.is_read ? (
                    <Button onClick={() => { markAsUnread(selectedLead.id); setSelectedLead({...selectedLead, is_read: false}); }} variant="outline" className="border-white/10 text-white">
                      Mark as Unread
                    </Button>
                  ) : (
                    <Button onClick={() => { markAsRead(selectedLead.id); setSelectedLead({...selectedLead, is_read: true}); }} className="bg-green-500 hover:bg-green-600 text-white">
                      Mark as Read
                    </Button>
                  )}
                  <Button onClick={() => deleteLead(selectedLead.id)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    Delete Lead
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
